import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import {
  symbolData,
  isCoinSymbol,
  isJackpotSymbol,
  isRoyalSymbol,
} from "../../../SymbolData";
import { ASSET_NAME } from "../../../assets";
import {
  formatToCustomLocale,
  attemptFullscreenToggle,
} from "../../../utilities";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import store from "../../../State/Store/store";
import {
  openSymbolPopup,
  closeSymbolPopup,
  closeMenu,
} from "../../../State/actions";
import { PixiSettings } from "../../../pixiSettings";
import { FONT_STYLES } from "../../../fontStyles";
import { Settings } from "../../../settings";

// Visible sprites start after top buffer (index 1) + landing zone (N symbols)
// Matches ReelControllerV2.js calculation: 1 + Settings.SYMBOLS_VISIBLE
const VISIBLE_SYMBOL_START_INDEX = 1 + Settings.SYMBOLS_VISIBLE;

// Configuration constants for info popup positioning, sizing, and styling
const INFO_SYMBOL_POPUP_CONFIG = {
  // Font sizes
  PAYTABLE_FONT_SIZE: 26,
  DESCRIPTION_FONT_SIZE: 19,
  LINE_HEIGHT_MULTIPLIER: 1.1,
  CONTENT_PADDING: 0.15,

  // Base dimensions before texture scaling
  POPUP_WIDTH_MULTIPLIER: 2.5,
  POPUP_HEIGHT_MULTIPLIER: 1.5,

  // Frame scaling (applied to popup dimensions)
  FRAME_WIDTH_SCALE: 1.0,
  FRAME_HEIGHT_SCALE: 1.1,

  // Positioning
  SYMBOL_Y_OFFSET_MULTIPLIER: -(0.5 * Settings.SYMBOLS_VISIBLE - 0.5), // Vertical center offset — matches OverlayReelSet formula

  // Coin value text (BitmapText)
  COIN_VALUE_FONT_SIZE_MULTIPLIER: 0.25, // fontSize = symbolHeight * this
  COIN_VALUE_MAX_WIDTH_MULTIPLIER: 0.9, // maxWidth = symbolHeight * this

  // Text container sizing
  TEXT_CONTAINER_WIDTH_MULTIPLIER: 0.9, // Increased to allow more width for descriptions
  TEXT_START_X_OFFSET_MULTIPLIER_LEFT: 0.5, // X offset multiplier for left side popups
  TEXT_START_X_OFFSET_MULTIPLIER_RIGHT: 0.5, // X offset multiplier for right side popups

  // Text spacing
  TEXT_GAP_MULTIPLIER: 0.3, // gap = fontSize * this

  // Texture offsets (for left/right side positioning)
  POPUP_TEXTURE_X_OFFSET_LEFT: -0.2,
  POPUP_TEXTURE_Y_OFFSET_LEFT: -0.2,
  POPUP_TEXTURE_X_OFFSET_RIGHT: 0.2,
  POPUP_TEXTURE_Y_OFFSET_RIGHT: -0.2,
};

/**
 * MiniPaytableController - Manages symbol info popups dynamically
 * Creates popup sprites on-demand when symbols are clicked
 * Only one popup can be active at a time
 */
class MiniPaytableController {
  constructor(
    overlayContainer,
    dimensions,
    reelRefs,
    t, // i18n translation function
    bet, // Current bet value
    overlayReelRefs, // Overlay reel refs for checking expanding wild state
  ) {
    this.overlayContainer = overlayContainer;
    this.dimensions = dimensions;
    this.reelRefs = reelRefs;
    this.overlayReelRefs = overlayReelRefs;
    this.t = t;
    this.bet = bet || 1; // Store bet value
    this.baseBet = 1; // Base bet for paytable calculations (will be updated via updateBaseBet())

    // Active popup state
    this.activePopup = null;
    this.activeSymbolPosition = null; // "reelIndex-symbolIndex"

    // Clickable areas (always present, lightweight)
    this.clickableAreas = [];

    // Cached textures
    this.infoFrameTexture = PIXI.Assets.get(ASSET_NAME.mini_paytable);

    // Track device type for desktop-specific behavior
    this.device = dimensions.device;

    // Internal caches for performance optimization
    this._symbolTypeCache = new Map(); // Cache symbol type lookups
    this._paytableScaleFactorCache = new Map(); // Cache paytable scale factor calculations
    this._framePositionCache = null; // Cache frame position calculations
    this._lastFrameDimensions = null; // Track dimensions for frame cache invalidation

    // Create clickable areas (REEL_COUNT × SYMBOLS_VISIBLE)
    this._createClickableAreas();
  }

  /**
   * Creates invisible clickable sprites over each symbol position
   * Creates clickable areas dynamically based on Settings (REEL_COUNT × SYMBOLS_VISIBLE)
   */
  _createClickableAreas() {
    const { symbolWidth, symbolHeight, reelSpacing } = this.dimensions;

    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const clickArea = new PIXI.Sprite(PIXI.Texture.EMPTY);
        clickArea.width = symbolWidth;
        clickArea.height = symbolHeight;
        clickArea.anchor.set(0.5);
        clickArea.alpha = 0.01; // Nearly invisible but still interactive
        clickArea.eventMode = "static";
        clickArea.cursor = "pointer";
        clickArea.zIndex = 10; // Above overlay background

        // Position based on reel grid — dynamic centering for any REEL_COUNT
        const centerOffset = (Settings.REEL_COUNT - 1) / 2;
        clickArea.x = Math.round(reelSpacing * (reelIndex - centerOffset));
        clickArea.y = Math.round(
          symbolHeight * symbolIndex +
            symbolHeight * INFO_SYMBOL_POPUP_CONFIG.SYMBOL_Y_OFFSET_MULTIPLIER,
        );

        // Store indices for click handler
        clickArea.reelIndex = reelIndex;
        clickArea.symbolIndex = symbolIndex;

        clickArea.on("pointerdown", (event) => {
          this.handleSymbolClick(reelIndex, symbolIndex);
          event.stopPropagation();
        });

        this.clickableAreas.push(clickArea);
        this.overlayContainer.addChild(clickArea);
      }
    }
  }

  /**
   * Handles symbol click
   * Desktop: Always opens popup (can switch between symbols)
   * Mobile/Tablet: If popup is open, close it. Otherwise open it.
   */
  handleSymbolClick(reelIndex, symbolIndex) {
    // Close menu if open
    store.dispatch(closeMenu());

    // Attempt to toggle fullscreen if conditions are met
    attemptFullscreenToggle(store.dispatch, this.device);

    soundManager.play(SOUND_NAME.button_click);

    // On mobile/tablet: if any popup is open, just close it
    if (!this.isDesktop() && this.activePopup) {
      this.closePopup();
      return;
    }

    // Desktop or no popup open: open the popup
    this.openPopup(reelIndex, symbolIndex);
  }

  /**
   * Opens popup - creates all sprites dynamically on-demand
   * Creates frame sprite, symbol texture, coin value text (if coin), and paytable/description text
   */
  openPopup(reelIndex, symbolIndex) {
    // Close existing popup first
    this.closePopup();

    const positionId = `${reelIndex}-${symbolIndex}`;
    this.activeSymbolPosition = positionId;

    // Get symbol data from reel controller
    const reelSymbolData = this._getSymbolDataFromReel(reelIndex, symbolIndex);

    // Check if we have valid symbol data
    // symbolId can be 0 (valid), so check explicitly for null/undefined
    // Also verify the symbol exists in SymbolData and has a texture
    const hasValidSymbolId =
      reelSymbolData.symbolId !== null && reelSymbolData.symbolId !== undefined;
    const symbolExists =
      hasValidSymbolId && symbolData[reelSymbolData.symbolId];
    const hasTexture =
      symbolExists && symbolData[reelSymbolData.symbolId].texture;

    if (
      !reelSymbolData.texture ||
      !hasValidSymbolId ||
      !symbolExists ||
      !hasTexture
    ) {
      return; // Can't create popup without data
    }

    // Create popup container
    const popupContainer = new PIXI.Container();
    popupContainer.name = "active_info_popup";
    popupContainer.sortableChildren = true;
    popupContainer.zIndex = 20; // Above clickable areas

    // Position popup container at symbol position
    const { symbolWidth, symbolHeight, reelSpacing } = this.dimensions;
    const centerOffset = (Settings.REEL_COUNT - 1) / 2;
    popupContainer.x = Math.round(reelSpacing * (reelIndex - centerOffset));
    popupContainer.y = Math.round(
      symbolHeight * symbolIndex +
        symbolHeight * INFO_SYMBOL_POPUP_CONFIG.SYMBOL_Y_OFFSET_MULTIPLIER,
    );

    // Calculate dimensions
    const popupWidth =
      symbolWidth * INFO_SYMBOL_POPUP_CONFIG.POPUP_WIDTH_MULTIPLIER;
    const popupHeight =
      symbolHeight * INFO_SYMBOL_POPUP_CONFIG.POPUP_HEIGHT_MULTIPLIER;
    const isLeftSide = reelIndex < Math.floor(Settings.REEL_COUNT / 2);

    // Set up container for desktop hover detection - match stone frame dimensions and position
    if (this.isDesktop()) {
      // Use the base popup dimensions to match the stone frame content area
      const hitWidth = popupWidth;
      const hitHeight = popupHeight;

      // Calculate frame position offset (same as frameSprite positioning)
      let popupX;
      if (isLeftSide) {
        popupX = -symbolWidth / 2 - this.dimensions.dividerDimensions.width;
      } else {
        popupX =
          symbolWidth / 2 -
          popupWidth +
          this.dimensions.dividerDimensions.width;
      }

      const frameXOffset = Math.round(
        popupX +
          popupWidth / 2 +
          symbolWidth *
            (isLeftSide
              ? INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_X_OFFSET_LEFT
              : INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_X_OFFSET_RIGHT),
      );

      const frameYOffset = Math.round(
        -symbolHeight / 2 -
          this.dimensions.dividerDimensions.width +
          popupHeight / 2 +
          symbolHeight *
            (isLeftSide
              ? INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_Y_OFFSET_LEFT
              : INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_Y_OFFSET_RIGHT),
      );

      popupContainer.eventMode = "static";
      popupContainer.hitArea = new PIXI.Rectangle(
        frameXOffset - hitWidth / 2,
        frameYOffset - hitHeight / 2,
        hitWidth,
        hitHeight,
      );

      popupContainer.on("pointerleave", () => {
        this.closePopup();
      });
    } else {
      popupContainer.eventMode = "none";
    }

    // Create frame sprite
    const frameSprite = this._createFrameSprite(
      popupWidth,
      popupHeight,
      isLeftSide,
    );
    popupContainer.addChild(frameSprite);

    // ── Symbol display: try spine clone first, fall back to static texture ──
    const spineState = this._getSpineStateFromOverlay(
      reelIndex,
      symbolIndex,
      reelSymbolData.symbolId,
    );

    let symbolSprite = null;
    let spineClone = null;
    let coinValueText = null;
    let coinValueContainer = null;

    if (spineState) {
      spineClone = this._createSpineClone(
        reelSymbolData.symbolId,
        spineState.animName,
        spineState.trackTime,
        spineState.isLooping,
      );
    }

    if (spineClone) {
      // Spine clone — live animation continues from exact captured frame
      spineClone.x = 0;
      spineClone.y = 0;
      spineClone.zIndex = 20;
      popupContainer.addChild(spineClone);

      // Coin value: inject into spine slot (matches OverlayReelController)
      const symbolInfo = symbolData[reelSymbolData.symbolId];
      if (
        symbolInfo?.value &&
        reelSymbolData.coinValueText &&
        !isJackpotSymbol(reelSymbolData.symbolId)
      ) {
        coinValueContainer = this._createCoinValueInSpineSlot(
          spineClone,
          reelSymbolData.coinValueText,
        );
      }
    } else {
      // Fall back to static texture (existing behavior)
      symbolSprite = new PIXI.Sprite(reelSymbolData.texture);
      symbolSprite.anchor.set(0.5);
      symbolSprite.scale.set(reelSymbolData.scale);
      symbolSprite.x = 0;
      symbolSprite.y = 0;
      symbolSprite.zIndex = 20;
      symbolSprite.name = "info_symbol_texture";
      popupContainer.addChild(symbolSprite);

      // Coin value: standalone BitmapText (existing behavior)
      const symbolInfo = symbolData[reelSymbolData.symbolId];
      if (symbolInfo?.value && reelSymbolData.coinValueText) {
        const displayValue = reelSymbolData.coinValueText;
        if (displayValue) {
          const { symbolHeight: sh, symbolWidth: sw } = this.dimensions;
          const fontSize = Math.round(
            sh * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER,
          );
          coinValueText = new PIXI.BitmapText(displayValue, {
            fontName: "font_secondary_scaled",
            fontSize: fontSize,
          });
          coinValueText.anchor.set(0.5);
          coinValueText.name = "coin_value";
          coinValueText.zIndex = 21;
          coinValueText.roundPixels = true;
          coinValueText.x = 0;
          const COIN_VALUE_Y_OFFSET = 82.67 / 338;
          coinValueText.y = sh * COIN_VALUE_Y_OFFSET;

          coinValueText.scale.set(1);
          coinValueText.updateText();

          const paddingPercent = 0.12;
          const maxTextWidth = sw * (1 - paddingPercent * 2);
          if (coinValueText.width > maxTextWidth) {
            coinValueText.scale.set(maxTextWidth / coinValueText.width);
          }

          popupContainer.addChild(coinValueText);
        }
      }
    }

    // Create text content
    const textElements = this._createTextContent(
      reelSymbolData.symbolId,
      isLeftSide,
      popupWidth,
      popupHeight,
    );
    textElements.forEach((el) => popupContainer.addChild(el));

    // Store reference for cleanup and bet updates
    this.activePopup = {
      container: popupContainer,
      frameSprite,
      symbolSprite,
      spineClone,
      coinValueText,
      coinValueContainer,
      textElements,
      reelIndex,
      symbolIndex,
      symbolId: reelSymbolData.symbolId,
    };

    this.overlayContainer.addChild(popupContainer);

    // Dispatch Redux action
    store.dispatch(openSymbolPopup(positionId));
  }

  /**
   * Closes popup - destroys all dynamically created sprites
   * Cleans up frame, symbol texture, coin value text, and paytable text elements
   */
  closePopup() {
    if (!this.activePopup) {
      return;
    }

    // Remove from overlay
    this.overlayContainer.removeChild(this.activePopup.container);

    // Clean up spine clone (detach slot objects before destroy to prevent localTransform errors)
    if (this.activePopup.spineClone) {
      if (this.activePopup.coinValueContainer) {
        try {
          this.activePopup.spineClone.removeSlotObject(
            "txt_slot_value",
            this.activePopup.coinValueContainer,
          );
        } catch {
          // Silent — spine may already be in cleanup
        }
      }
      if (this.activePopup.spineClone.state) {
        this.activePopup.spineClone.state.clearTracks();
      }
      this.activePopup.spineClone.autoUpdate = false;
    }

    // Destroy all sprites
    this.activePopup.frameSprite.destroy({ children: true });
    if (this.activePopup.symbolSprite) {
      this.activePopup.symbolSprite.destroy();
    }
    if (this.activePopup.coinValueText) {
      this.activePopup.coinValueText.destroy();
    }
    if (
      this.activePopup.coinValueContainer &&
      !this.activePopup.coinValueContainer.destroyed
    ) {
      this.activePopup.coinValueContainer.destroy({ children: true });
    }
    this.activePopup.textElements.forEach((el) => el.destroy());
    this.activePopup.container.destroy({ children: true });

    this.activePopup = null;
    this.activeSymbolPosition = null;

    // Dispatch Redux action
    store.dispatch(closeSymbolPopup());
  }

  /**
   * Creates the decorative frame sprite for the popup
   * Positions frame on left or right side based on reel position
   */
  _createFrameSprite(popupWidth, popupHeight, isLeftSide) {
    const { symbolWidth, symbolHeight, dividerDimensions } = this.dimensions;

    const frameSprite = new PIXI.Sprite(this.infoFrameTexture);
    frameSprite.anchor.set(0.5);
    frameSprite.width = popupWidth * INFO_SYMBOL_POPUP_CONFIG.FRAME_WIDTH_SCALE;
    frameSprite.height =
      popupHeight * INFO_SYMBOL_POPUP_CONFIG.FRAME_HEIGHT_SCALE;
    frameSprite.name = "info_popup_visual";

    // Calculate frame position (matches InfoSymbolPopup logic)
    let popupX;
    if (isLeftSide) {
      popupX = -symbolWidth / 2 - dividerDimensions.width;
    } else {
      popupX = symbolWidth / 2 - popupWidth + dividerDimensions.width;
    }

    frameSprite.x = Math.round(
      popupX +
        popupWidth / 2 +
        symbolWidth *
          (isLeftSide
            ? INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_X_OFFSET_LEFT
            : INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_X_OFFSET_RIGHT),
    );

    frameSprite.y = Math.round(
      -symbolHeight / 2 -
        dividerDimensions.width +
        popupHeight / 2 +
        symbolHeight *
          (isLeftSide
            ? INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_Y_OFFSET_LEFT
            : INFO_SYMBOL_POPUP_CONFIG.POPUP_TEXTURE_Y_OFFSET_RIGHT),
    );

    return frameSprite;
  }

  /**
   * Gets symbol texture, scale, and ID from Redux state and rendered sprite
   * If expanding wild is active on this reel, returns wild symbol instead
   * Uses currentRound.reels from Redux state
   * Retrieves actual texture and scale from ReelControllerV2's rendered sprite
   */
  _getSymbolDataFromReel(reelIndex, symbolIndex) {
    // Check if expanding wild is active on this reel
    const overlayReel = this.overlayReelRefs?.current?.[reelIndex];
    const overlayController = overlayReel?.getOverlayReelController?.();

    // If expanding wild is visible, return wild symbol (ID 1)
    if (overlayController?.isExpandingWildVisible) {
      const WILD_SYMBOL_ID = 1;
      const symbolInfo = symbolData[WILD_SYMBOL_ID];

      if (!symbolInfo?.texture) {
        return { texture: null, scale: 1, symbolId: WILD_SYMBOL_ID };
      }

      // Get wild texture directly from symbolData
      const textureName = symbolInfo.texture;
      const reel = this.reelRefs.current[reelIndex];
      const controller = reel?.getReelController();

      if (!controller?.spritesheet?.textures) {
        return { texture: null, scale: 1, symbolId: WILD_SYMBOL_ID };
      }

      // Get the wild texture from spritesheet
      const wildTexture = controller.spritesheet.textures[`${textureName}.png`];

      if (!wildTexture) {
        return { texture: null, scale: 1, symbolId: WILD_SYMBOL_ID };
      }

      // Get scale from any visible sprite
      const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
      const spriteData = controller.spinningSprites?.[spriteIndex];
      const scale = spriteData?.normalSprite?.scale?.x || 1;

      return {
        texture: wildTexture,
        scale: scale,
        symbolId: WILD_SYMBOL_ID,
      };
    }

    // Read symbol data directly from the rendered sprite on screen
    // This ensures we always show info for what's actually visible, not from Redux state
    const reel = this.reelRefs.current[reelIndex];
    const controller = reel?.getReelController();

    if (!controller?.spinningSprites) {
      return { texture: null, scale: 1, symbolId: null };
    }

    // Visible sprites start at VISIBLE_SYMBOL_START_INDEX (matches ReelControllerV2)
    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    const spriteData = controller.spinningSprites[spriteIndex];

    if (!spriteData?.normalSprite) {
      return { texture: null, scale: 1, symbolId: null };
    }

    // Get the actual symbol ID from the rendered sprite (not from Redux)
    const symbolId = spriteData.currentSymbolId;

    if (symbolId === undefined || symbolId === null) {
      return { texture: null, scale: 1, symbolId: null };
    }

    // Get symbol info from SymbolData
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) {
      return { texture: null, scale: 1, symbolId: null };
    }

    // Check if symbol has texture (blank symbols don't)
    const textureName = symbolInfo.texture;
    if (!textureName || textureName === null) {
      return { texture: null, scale: 1, symbolId };
    }

    return {
      texture: spriteData.normalSprite.texture,
      scale: spriteData.normalSprite.scale?.x || 1,
      symbolId: symbolId,
      coinValueText: spriteData.valueText?.text || null, // Get the actual coin value from the reel
    };
  }

  // ── Spine Clone Methods ───────────────────────────────────────────────

  /**
   * Read the OverlayReelController for the given reel/symbol position
   * and extract the current spine animation state (name + exact frame).
   * Returns null if no spine is active → caller should use static texture.
   */
  _getSpineStateFromOverlay(reelIndex, symbolIndex, symbolId) {
    const overlayReel = this.overlayReelRefs?.current?.[reelIndex];
    const controller = overlayReel?.getOverlayReelController?.();
    if (!controller) return null;

    // Expanding wild is a full-reel spine — don't clone it into a per-symbol popup
    if (controller.isExpandingWildVisible) return null;

    // Check active animations map
    const animData = controller.activeAnimations?.get(symbolIndex);
    if (!animData) return null;

    const { spineInstance, symbolId: activeSymbolId } = animData;

    // Verify symbol match
    if (activeSymbolId !== symbolId) return null;

    // Verify spine is actually visible and ticking
    if (
      !spineInstance ||
      spineInstance.alpha < 0.01 ||
      !spineInstance.autoUpdate
    ) {
      return null;
    }

    // Capture exact animation state
    const currentTrack = spineInstance.state?.getCurrent(0);
    if (!currentTrack?.animation) return null;

    return {
      animName: currentTrack.animation.name,
      trackTime: currentTrack.trackTime || 0,
      isLooping: currentTrack.loop,
    };
  }

  /**
   * Create a fresh Spine instance that starts at the exact captured animation frame.
   * Same technique as CoinCollectAnimator — seamless visual handoff.
   *
   * @param {number} symbolId - Symbol ID to create spine for
   * @param {string} sourceAnimName - Animation name captured from overlay
   * @param {number} sourceTrackTime - Exact track time for frame-perfect match
   * @param {boolean} isLooping - Whether the animation should loop
   * @returns {Spine|null}
   */
  _createSpineClone(
    symbolId,
    sourceAnimName,
    sourceTrackTime,
    isLooping = true,
  ) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo?.spine) return null;

    const spineName = symbolInfo.spine;
    const skeletonAlias = `${spineName}-skeleton`;
    const atlasAlias = `${spineName}-atlas`;
    const assets = PIXI.Assets.cache;

    if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

    try {
      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!spine?.skeleton || !spine?.state) return null;

      // Apply skin (shared spines like "c", "j", "lp" use skins to differentiate)
      if (symbolInfo.skin && spine.skeleton?.data) {
        const skin = spine.skeleton.data.findSkin(symbolInfo.skin);
        if (skin) {
          spine.skeleton.setSkin(skin);
          spine.skeleton.setSlotsToSetupPose();
        }
      } else if (symbolInfo.name && spine.skeleton?.data) {
        // Try symbol name as skin (coins: c1, c2, ..., c15)
        const skin = spine.skeleton.data.findSkin(symbolInfo.name);
        if (skin) {
          spine.skeleton.setSkin(skin);
          spine.skeleton.setSlotsToSetupPose();
        }
      }

      // Scale to match current symbol dimensions (single lookup — no padding math)
      const { symbolHeight } = this.dimensions;
      const symbolInfo2 = symbolData[symbolId];
      const sPay = symbolInfo2?.symbolPay || '';
      const categoryScale = Settings.SYMBOL_SCALE_OVERRIDES?.[sPay]
        ?? Settings.SYMBOL_SCALE_OVERRIDES?.DEFAULT
        ?? 1;
      const spineScale = (symbolHeight / Settings.SYMBOL_HEIGHT) * categoryScale;
      spine.scale.set(spineScale);

      // Prevent reset-pose flash on first frame
      spine.autoUpdate = false;

      // Apply animation at exact captured frame
      if (sourceAnimName && spine.state) {
        const entry = spine.state.setAnimation(0, sourceAnimName, isLooping);
        if (entry) {
          entry.trackTime = sourceTrackTime;
        }

        // Force-render the correct frame before first draw
        spine.state.update(0);
        spine.state.apply(spine.skeleton);
        spine.skeleton.updateWorldTransform(0);
      }

      // Now safe to auto-update — animation continues from captured frame
      spine.autoUpdate = true;
      spine.name = "info_spine_clone";

      return spine;
    } catch {
      return null;
    }
  }

  /**
   * Inject coin value text into the spine clone's txt_slot_value slot.
   * Matches OverlayReelController.showCoinValueText — compensates for
   * spine PIXI scale and bone world scale so text renders at correct size.
   *
   * @param {Spine} spineClone - The cloned spine instance
   * @param {string} displayValue - Formatted coin value string
   * @returns {PIXI.Container|null} The coin value container (for cleanup)
   */
  _createCoinValueInSpineSlot(spineClone, displayValue) {
    if (!displayValue || !spineClone) return null;

    const counterContainer = new PIXI.Container();
    counterContainer.name = "coin_value_text";
    counterContainer.alpha = 1;
    counterContainer.zIndex = 1000;

    const { symbolHeight, symbolWidth } = this.dimensions;
    const fontSize = symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;

    const bitmapText = new PIXI.BitmapText(displayValue, {
      fontName: "font_secondary_scaled",
      fontSize: fontSize,
    });
    bitmapText.anchor.set(0.5);
    bitmapText.roundPixels = true;
    bitmapText.updateText();

    // Counter spine PIXI scale + bone world scale (same as OverlayReelController).
    // update(0) forces bone world transforms to compute without advancing time.
    const spineScale = spineClone.scale.x || 1;
    spineClone.update(0);
    const valueBone = spineClone.skeleton?.findBone("value");
    const boneBaseScale = Math.abs(valueBone?.a) || 1;
    const totalScale = spineScale * boneBaseScale;

    if (totalScale && totalScale !== 0) {
      bitmapText.fontSize = bitmapText.fontSize / totalScale;
      bitmapText.updateText();
    }

    // Auto-fit: shrink if text overflows symbol width (in spine-local units)
    const paddingPercent = 0.12;
    const maxTextWidth =
      totalScale !== 0
        ? (symbolWidth * (1 - paddingPercent * 2)) / totalScale
        : symbolWidth * (1 - paddingPercent * 2);
    if (bitmapText.width > maxTextWidth) {
      bitmapText.scale.set(maxTextWidth / bitmapText.width);
    }

    counterContainer.addChild(bitmapText);
    spineClone.addSlotObject("txt_slot_value", counterContainer);

    return counterContainer;
  }

  /**
   * Creates text content based on symbol type
   * Returns description text for wild/scatter/coin, or paytable entries for regular symbols
   */
  _createTextContent(symbolId, isLeftSide, popupWidth, popupHeight) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) return [];

    const state = store.getState();
    const paytable = state.game.paytable;
    const bet = state.game.bet;
    const currency = state.game.currency;
    const { symbolScale } = this.dimensions;

    // Determine symbol type
    const symbolType = this._getSymbolType(symbolId, symbolInfo);

    // Calculate text container position
    const textContainerWidth =
      this.dimensions.symbolWidth *
      INFO_SYMBOL_POPUP_CONFIG.TEXT_CONTAINER_WIDTH_MULTIPLIER;
    const textContainerX = isLeftSide
      ? this.dimensions.symbolWidth
      : -this.dimensions.symbolWidth;

    if (symbolType === "wild") {
      return this._createDescriptionText(
        this.t("wild_minipaytable", { ns: "common" }),
        textContainerX,
        textContainerWidth,
        symbolScale,
      );
    }

    if (symbolType === "scatter") {
      return this._createDescriptionText(
        this.t("scatter_minipaytable", { ns: "common" }),
        textContainerX,
        textContainerWidth,
        symbolScale,
      );
    }

    if (symbolType === "coin") {
      return this._createDescriptionText(
        this.t("coin_minipaytable", { ns: "common" }),
        textContainerX,
        textContainerWidth,
        symbolScale,
      );
    }

    if (symbolType === "royal") {
      return this._createDescriptionText(
        this.t("royal_minipaytable", { ns: "common" }),
        textContainerX,
        textContainerWidth,
        symbolScale,
      );
    }

    // Regular symbol - create paytable
    return this._createPaytableText(
      symbolId,
      paytable,
      bet,
      currency,
      textContainerX,
      textContainerWidth,
      symbolScale,
      isLeftSide,
    );
  }

  /**
   * Determines symbol type using SymbolData helper functions
   * Returns: "wild", "scatter", "coin", "royal", or "regular"
   * Cached for performance
   */
  _getSymbolType(symbolId, symbolInfo) {
    if (!symbolInfo) return "regular";

    // Check cache first
    if (this._symbolTypeCache.has(symbolId)) {
      return this._symbolTypeCache.get(symbolId);
    }

    let symbolType = "regular";
    const numericId =
      typeof symbolId === "string" ? parseInt(symbolId, 10) : symbolId;

    // Check symbol type — use helper functions first (handle null symbolPay),
    // then fall back to symbolPay field for legacy compatibility
    if (symbolInfo.symbolPay === "wild") {
      symbolType = "wild";
    } else if (symbolInfo.symbolPay === "scatter") {
      symbolType = "scatter";
    } else if (isRoyalSymbol(numericId)) {
      symbolType = "royal";
    } else if (isCoinSymbol(numericId) || isJackpotSymbol(numericId)) {
      symbolType = "coin";
    }
    // Everything else is regular (hp, mp, lp)

    // Cache result
    this._symbolTypeCache.set(symbolId, symbolType);

    return symbolType;
  }


  /**
   * Creates description text for wild/scatter/coin symbols.
   * Text renders at a fixed native size inside a container scaled by symbolScale,
   * ensuring identical visual quality across all screen sizes.
   */
  _createDescriptionText(
    text,
    textContainerX,
    textContainerWidth,
    symbolScale,
  ) {
    const baseFontSize = INFO_SYMBOL_POPUP_CONFIG.DESCRIPTION_FONT_SIZE;

    // Word wrap width is in unscaled coordinates (container handles scaling)
    const style = {
      ...FONT_STYLES.miniPaytable,
      fontSize: baseFontSize,
      align: "center",
      wordWrap: true,
      wordWrapWidth: Math.round(textContainerWidth / symbolScale),
    };

    const textSprite = new PIXI.Text(text, style);
    textSprite.anchor.set(0.5);
    textSprite.roundPixels = true;
    textSprite.name = "info_popup_description";

    // Wrap in a scaled container so text looks identical everywhere
    const textContainer = new PIXI.Container();
    textContainer.name = "description_text_container";
    textContainer.scale.set(symbolScale);
    textContainer.x = Math.round(textContainerX);
    textContainer.y = 0;
    textContainer.addChild(textSprite);

    return [textContainer];
  }

  /**
   * Creates paytable text entries for regular symbols.
   * Text renders at a fixed native size inside a container scaled by symbolScale,
   * ensuring identical visual quality across all screen sizes.
   * Format: "5: 1.00 FUN" (symbol count : formatted payout currency)
   */
  _createPaytableText(
    symbolId,
    paytable,
    bet,
    currency,
    textContainerX,
    textContainerWidth,
    symbolScale,
    isLeftSide,
  ) {
    if (!paytable || !paytable[symbolId]) return [];

    // Build display entries
    const displayEntries = paytable[symbolId]
      .map((value, idx) => ({ value, originalIndex: idx }))
      .reverse()
      .filter((item) => item.value !== 0)
      .map((item, displayIndex) => {
        const symbolCount = paytable[symbolId].length - displayIndex;
        const payoutAmount = item.value * (bet / this.baseBet);
        const formattedPayout = formatToCustomLocale(payoutAmount);
        return { symbolCount, formattedPayout };
      });

    if (displayEntries.length === 0) return [];

    // Use fixed base font size (container scaling handles responsiveness)
    const baseFontSize = INFO_SYMBOL_POPUP_CONFIG.PAYTABLE_FONT_SIZE;
    const lineHeight =
      baseFontSize * INFO_SYMBOL_POPUP_CONFIG.LINE_HEIGHT_MULTIPLIER;

    // Available width in unscaled coordinates
    const unscaledContainerWidth = Math.round(textContainerWidth / symbolScale);

    // Calculate scale factor to fit text in unscaled space
    const paytableScaleFactor = this._calculatePaytableScaleFactor(
      displayEntries,
      baseFontSize,
      unscaledContainerWidth,
      currency,
    );

    const adjustedFontSize = Math.round(baseFontSize * paytableScaleFactor);
    const adjustedGap =
      adjustedFontSize * INFO_SYMBOL_POPUP_CONFIG.TEXT_GAP_MULTIPLIER;

    // Shared gradient style
    const lineStyle = {
      ...FONT_STYLES.miniPaytable,
      fontSize: adjustedFontSize,
      align: "left",
    };

    // Calculate vertical positioning in unscaled coordinates
    const totalContentHeight = displayEntries.length * lineHeight;
    const startY = -totalContentHeight / 2;
    const offsetMultiplier = isLeftSide
      ? INFO_SYMBOL_POPUP_CONFIG.TEXT_START_X_OFFSET_MULTIPLIER_LEFT
      : INFO_SYMBOL_POPUP_CONFIG.TEXT_START_X_OFFSET_MULTIPLIER_RIGHT;
    // textContainerX and textContainerWidth are in scaled space,
    // convert to unscaled for positioning inside the container
    const unscaledTextContainerX = Math.round(textContainerX / symbolScale);
    const textStartX =
      unscaledTextContainerX - unscaledContainerWidth * offsetMultiplier;

    // Create text sprites inside a scaled container
    const textContainer = new PIXI.Container();
    textContainer.name = "paytable_text_container";
    textContainer.scale.set(symbolScale);

    displayEntries.forEach((entry, displayIndex) => {
      const lineY = startY + displayIndex * lineHeight;
      const symbolCountText = `${entry.symbolCount}x`;
      const payoutText = `${entry.formattedPayout} ${currency || ""}`.trim();

      // Create symbol count text
      const symbolCountSprite = new PIXI.Text(symbolCountText, lineStyle);
      symbolCountSprite.x = Math.round(textStartX);
      symbolCountSprite.y = Math.round(lineY);
      symbolCountSprite.roundPixels = true;
      symbolCountSprite.name = `paytable_symbol_count_${displayIndex}`;
      textContainer.addChild(symbolCountSprite);

      // Create payout text
      const symbolCountWidth = symbolCountSprite.width;
      const payoutX = Math.round(textStartX + symbolCountWidth + adjustedGap);
      const payoutSprite = new PIXI.Text(payoutText, lineStyle);
      payoutSprite.x = payoutX;
      payoutSprite.y = Math.round(lineY);
      payoutSprite.roundPixels = true;
      payoutSprite.name = `paytable_payout_${displayIndex}`;
      textContainer.addChild(payoutSprite);
    });

    return [textContainer];
  }

  /**
   * Calculates scale factor to fit paytable text within container width
   * Scales down font size if text would overflow
   * Cached for performance - invalidated when dimensions or entries change
   */
  _calculatePaytableScaleFactor(
    displayEntries,
    fontSize,
    availableWidth,
    currency,
  ) {
    // Create cache key from inputs (include payout text so bet changes trigger recalculation)
    const payoutKeys = displayEntries.map((e) => e.formattedPayout).join("_");
    const cacheKey = `${displayEntries.length}_${fontSize}_${availableWidth}_${currency}_${payoutKeys}`;

    // Check cache first
    if (this._paytableScaleFactorCache.has(cacheKey)) {
      return this._paytableScaleFactorCache.get(cacheKey);
    }

    const baseStyle = new PIXI.TextStyle({
      ...FONT_STYLES.miniPaytable,
      fontSize: fontSize,
    });

    let minScaleFactor = 1;

    displayEntries.forEach((entry) => {
      const symbolCountText = `${entry.symbolCount}x`;
      const payoutText = `${entry.formattedPayout} ${currency || ""}`.trim();

      const symbolCountWidth = PIXI.TextMetrics.measureText(
        symbolCountText,
        baseStyle,
      ).width;
      const payoutWidth = PIXI.TextMetrics.measureText(
        payoutText,
        baseStyle,
      ).width;
      const gap = fontSize * INFO_SYMBOL_POPUP_CONFIG.TEXT_GAP_MULTIPLIER;
      const totalTextWidth = symbolCountWidth + gap + payoutWidth;

      if (totalTextWidth > availableWidth) {
        const scaleFactor = availableWidth / totalTextWidth;
        minScaleFactor = Math.min(minScaleFactor, scaleFactor);
      }
    });

    // Cache result
    this._paytableScaleFactorCache.set(cacheKey, minScaleFactor);

    return minScaleFactor;
  }

  /**
   * Updates clickable area positions and sizes on resize
   * Maintains grid alignment with symbol positions
   */
  _updateClickableAreaPositions() {
    const { symbolWidth, symbolHeight, reelSpacing } = this.dimensions;

    let areaIndex = 0;
    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const clickArea = this.clickableAreas[areaIndex];
        if (clickArea) {
          clickArea.width = symbolWidth;
          clickArea.height = symbolHeight;
          const centerOffset = (Settings.REEL_COUNT - 1) / 2;
          clickArea.x = Math.round(reelSpacing * (reelIndex - centerOffset));
          clickArea.y = Math.round(
            symbolHeight * symbolIndex +
              symbolHeight *
                INFO_SYMBOL_POPUP_CONFIG.SYMBOL_Y_OFFSET_MULTIPLIER,
          );
        }
        areaIndex++;
      }
    }
  }

  /**
   * Handles resize - updates clickable areas and recreates active popup
   * Ensures popup maintains correct position and scale after dimension changes
   */
  resize(newDimensions) {
    this.dimensions = newDimensions;
    this.device = newDimensions.device;

    // Invalidate caches when dimensions change
    this._framePositionCache = null;
    this._lastFrameDimensions = null;
    this._paytableScaleFactorCache.clear(); // Clear paytable cache on resize

    // Update clickable areas
    this._updateClickableAreaPositions();

    // Recreate active popup if exists
    if (this.activePopup) {
      const { reelIndex, symbolIndex } = this.activePopup;
      this.openPopup(reelIndex, symbolIndex);
    }
  }

  /**
   * Sets interactive state of clickable areas
   * Disables interaction during spin, autoplay, Hold & Win, or win animations
   */
  setInteractive(interactive) {
    this.clickableAreas.forEach((area) => {
      area.eventMode = interactive ? "static" : "none";
    });
  }

  /**
   * Checks if current device is desktop
   * @returns {boolean} True if desktop
   */
  isDesktop() {
    return this.device === PixiSettings.DEVICE.DESKTOP;
  }

  /**
   * Checks if popup is currently active
   * @returns {boolean} True if popup is open
   */
  hasActivePopup() {
    return !!this.activePopup;
  }

  /**
   * Updates bet value and refreshes popup if open
   * For coin symbols: Don't refresh (keep original values from completed spin)
   * For other symbols: Refresh with new bet-multiplied paytable values
   */
  updateBet(newBet) {
    this.bet = newBet || 1;

    // If popup is open, check if it's a coin symbol
    if (this.activePopup) {
      const { symbolId } = this.activePopup;

      // If it's a coin symbol, don't refresh
      // Coin values should remain frozen at their original values from the completed spin
      if (isCoinSymbol(symbolId)) {
        return; // Skip refresh for coins
      }

      // For non-coin symbols (paytable entries), refresh with new bet values
      const { reelIndex, symbolIndex } = this.activePopup;
      this.closePopup();
      this.openPopup(reelIndex, symbolIndex);
    }
  }

  /**
   * Updates base bet value and refreshes popup if open
   * Recreates popup with new paytable calculations (paytable_value * (bet / baseBet))
   */
  updateBaseBet(newBaseBet) {
    this.baseBet = newBaseBet || 1;

    // If popup is open, recreate it with new base bet values
    if (this.activePopup) {
      const { reelIndex, symbolIndex } = this.activePopup;
      this.closePopup();
      this.openPopup(reelIndex, symbolIndex);
    }
  }

  /**
   * Calculates display value for coin symbols (coinValue * bet) with locale formatting
   * @param {string} symbolValue - Coin value from SymbolData (e.g., "1", "2", "5")
   * @returns {string} Formatted display value (e.g., "1.00", "2.00") or empty string if invalid
   */
  _getCoinDisplayValue(symbolValue) {
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const coinValue = parseInt(symbolValue, 10);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      const displayValue = coinValue * this.bet;
      return formatToCustomLocale(displayValue);
    } catch (error) {
      return "";
    }
  }

  /**
   * Cleanup - destroys all sprites and removes event listeners
   * Called when component unmounts
   */
  destroy() {
    this.closePopup();
    this.clickableAreas.forEach((area) => {
      area.removeAllListeners();
      area.destroy();
    });
    this.clickableAreas = [];
  }
}

export default MiniPaytableController;
